import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { InlineLoadingSpinner } from '@spins-gcp/shared-components-react';
import styles from './dialog.module.scss';

export interface DialogAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'danger' | 'cancel';
}

interface DialogProps {
  isOpen?: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  actions?: DialogAction[];
  actionsAlign?: 'center' | 'end';
  children: React.ReactNode;
}

export function Dialog({
  isOpen = true,
  title,
  onClose,
  width = 480,
  actions,
  actionsAlign = 'end',
  children,
}: DialogProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles['overlay']} onClick={onClose}>
      <div
        className={styles['dialog']}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button
          className={styles['close-btn']}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className={styles['header']}>
          <h2 className={styles['title']}>{title}</h2>
        </div>

        {children}

        {actions && actions.length > 0 && (
          <div
            className={styles['actions']}
            style={{
              justifyContent: actionsAlign === 'center' ? 'center' : 'flex-end',
            }}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                className={styles[`btn-${action.variant ?? 'primary'}`]}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                aria-label={action.loading ? action.label : undefined}
              >
                {action.loading ? (
                  <InlineLoadingSpinner size={8} />
                ) : (
                  action.label
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
